import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar } from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import Axios from "axios";
import { toast, ToastContainer } from "react-toastify";

function splitAfterWords(text, wordLimit) {
  const words = text?.split(" ");
  if (words?.length > wordLimit) return words.slice(0, wordLimit).join(" ") + "...";
  return text;
}

const getClickableLink = (link) => {
  if (!link) return link;
  return link.startsWith("http://") || link.startsWith("https://") ? link : `https://${link}`;
};

const tagStyle = {
  fontFamily: "Nunito", fontSize: 12, fontWeight: 700, color: "#374151",
  background: "#f3f4f6", borderRadius: 20, padding: "4px 12px", whiteSpace: "nowrap",
};

const infoRow = { display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: "1px solid #f3f4f6" };
const labelStyle = { fontFamily: "Nunito", fontSize: 12, fontWeight: 700, color: "#9ca3af", minWidth: 100, marginTop: 2 };
const valueStyle = { fontFamily: "Nunito", fontSize: 14, color: "#111827", flex: 1, wordBreak: "break-word" };

const FounderPage = () => {
  const location = useLocation();
  const history = useNavigate();
  const { data } = location.state;
  const [founder] = useState(data);
  const [verified, setVerify] = useState(false);
  const [decline, setDecline] = useState(false);
  const [document, setdocument] = useState([]);
  const [similargd, setsimilargd] = useState([]);
  const [selectedSimilarGd, setselectedSimilarGd] = useState();
  const [review, setreview] = useState("");
  const [aiPrediction, setAiPrediction] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const handleVerify = async () => {
    if (!confirm("Are you sure you want to Verify this?")) return;
    founder.selectedSimilarGd = selectedSimilarGd ?? false;
    await Axios.post("/api/admin/handle-startups/accept", founder)
      .then(() => { toast.success("Successfully Verified"); setVerify(true); })
      .catch(() => { toast.error("Some Error"); setVerify(false); });
  };

  const handleDecline = async () => {
    if (!confirm("Are you sure you want to Decline this?")) return;
    await Axios.post("/api/admin/handle-startups/decline", founder)
      .then(() => { toast.warning("Successfully Declined"); setDecline(true); setTimeout(() => history("/admin/verify-founder"), 500); })
      .catch(() => { toast.error("Some Error"); setDecline(false); });
  };

  const fetchDocument = async () => {
    await Axios({ method: "GET", url: `/api/admin/documents/${founder.id}` })
      .then((res) => setdocument(res.data.documents))
      .catch(() => {});
  };

  const fetchSimilarGdStartups = async () => {
    await Axios({ method: "GET", url: `/api/admin/checkgdstartups/check/${founder.id}` })
      .then((res) => setsimilargd(res.data.data))
      .catch(() => {});
  };

  useEffect(() => { fetchDocument(); fetchSimilarGdStartups(); }, []);

  const runAiCheck = async () => {
    setAiLoading(true);
    setAiError(null);
    await Axios.get(`/api/admin/handle-startups/${founder.id}/predict-verification`)
      .then((res) => setAiPrediction(res.data.prediction))
      .catch((err) => setAiError(err.response?.data?.error || "AI check failed"))
      .finally(() => setAiLoading(false));
  };

  const verdictColors = {
    approved: { bg: "#ecfdf5", border: "#a7f3d0", text: "#047857" },
    needs_review: { bg: "#fffbeb", border: "#fde68a", text: "#b45309" },
    rejected: { bg: "#fef2f2", border: "#fecaca", text: "#b91c1c" },
  };

  const sendReview = async () => {
    if (!confirm("Are you sure you want to Send this review?")) return;
    await Axios.post("/api/admin/review", { id: founder.id, reason: review })
      .then(() => toast.success("Successfully Sent Review"))
      .catch(() => toast.error("Some error"));
  };

  const date = new Date(founder.incorporationDate);
  const tags = [founder.fundingSeries, founder.employees, founder.industry, founder.growth, founder.audience].filter(Boolean);

  return (
    <div style={{ padding: "36px 40px", maxWidth: 960, margin: "0 auto", boxSizing: "border-box" }}>
      <button
        onClick={() => history("/admin/verify-founder")}
        style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16, fontFamily: "Nunito", fontSize: 13, fontWeight: 700, color: "#374151", background: "none", border: "none", padding: 0, cursor: "pointer" }}>
        ← Back
      </button>

      {/* Header */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: "28px 32px", marginBottom: 24, display: "flex", alignItems: "flex-start", gap: 24 }}>
        <Avatar src={founder.logo} variant="rounded" style={{ width: 80, height: 80, border: "1px solid #e5e7eb", flexShrink: 0, borderRadius: 12 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontFamily: "Nunito", fontWeight: 800, fontSize: 28, color: "#111827", margin: "0 0 4px" }}>{founder.companyName}</h1>
          <p style={{ fontFamily: "Nunito", fontSize: 14, color: "#6b7280", margin: "0 0 12px" }}>
            {founder.location?.country}
            {founder.incorporationDate ? ` · Est. ${date.getFullYear()}` : ""}
          </p>
          {tags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {tags.map((t, i) => <span key={i} style={tagStyle}>{t}</span>)}
            </div>
          )}
          {founder.website && (
            <a href={getClickableLink(founder.website)} target="_blank" rel="noreferrer"
              style={{ fontFamily: "Nunito", fontSize: 13, color: "#3b82f6", textDecoration: "none" }}>
              {founder.website}
            </a>
          )}
        </div>
      </div>

      {/* Two-column body */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20, alignItems: "start" }}>
        {/* Left: details */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* About */}
          {founder.description && (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px" }}>
              <p style={{ fontFamily: "Nunito", fontWeight: 800, fontSize: 13, color: "#374151", margin: "0 0 10px", textTransform: "uppercase", letterSpacing: 1 }}>About</p>
              <p style={{ fontFamily: "Nunito", fontSize: 14, color: "#374151", lineHeight: 1.7, margin: 0 }}>{founder.description}</p>
            </div>
          )}

          {/* Founder info + Verify/Decline */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px" }}>
            <p style={{ fontFamily: "Nunito", fontWeight: 800, fontSize: 13, color: "#374151", margin: "0 0 10px", textTransform: "uppercase", letterSpacing: 1 }}>Founder Contact</p>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid #f3f4f6" }}>
              <Avatar src={founder.profilePic} style={{ width: 48, height: 48 }} />
              <p style={{ fontFamily: "Nunito", fontWeight: 700, fontSize: 15, color: "#111827", margin: 0 }}>{founder.name}</p>
            </div>
            <div style={infoRow}><span style={labelStyle}>Email</span><span style={valueStyle}>{founder.contact?.email}</span></div>
            <div style={infoRow}><span style={labelStyle}>Phone</span><span style={valueStyle}>{founder.contact?.phone}</span></div>
            <div style={infoRow}>
              <span style={labelStyle}>LinkedIn</span>
              {founder.socialConnects?.linkedin
                ? <a href={getClickableLink(founder.socialConnects.linkedin)} target="_blank" rel="noreferrer" style={{ ...valueStyle, color: "#3b82f6", textDecoration: "underline", wordBreak: "break-all" }}>{founder.socialConnects.linkedin}</a>
                : <span style={valueStyle}>—</span>}
            </div>
            {/* Verify / Decline inside contact card */}
            <div style={{ display: "flex", gap: 10, marginTop: 20, paddingTop: 16, borderTop: "1px solid #f3f4f6" }}>
              <button
                onClick={decline ? undefined : handleDecline}
                style={{ flex: 1, fontFamily: "Nunito", fontSize: 13, fontWeight: 700, background: "#fff", color: decline ? "#9ca3af" : "#dc2626", border: `1px solid ${decline ? "#e5e7eb" : "#dc2626"}`, borderRadius: 8, padding: "10px", cursor: decline ? "default" : "pointer" }}>
                {decline ? "Declined" : "Decline"}
              </button>
              <button
                onClick={verified ? undefined : handleVerify}
                style={{ flex: 1, fontFamily: "Nunito", fontSize: 13, fontWeight: 700, background: verified ? "#e5e7eb" : "#111827", color: verified ? "#9ca3af" : "#fff", border: "none", borderRadius: 8, padding: "10px", cursor: verified ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <DoneAllIcon style={{ fontSize: 16 }} />
                {verified ? "Verified" : "Verify"}
              </button>
            </div>
          </div>


          {/* Similar GD startups */}
          {similargd.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px" }}>
              <p style={{ fontFamily: "Nunito", fontWeight: 800, fontSize: 13, color: "#374151", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: 1 }}>Global Database Matches</p>
              <p style={{ fontFamily: "Nunito", fontSize: 12, color: "#9ca3af", margin: "0 0 16px" }}>Selecting a match will update the existing GD entry instead of creating a new one.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {similargd.map((gd, i) => (
                  <div key={i} onClick={() => setselectedSimilarGd(gd._id === selectedSimilarGd ? null : gd._id)}
                    style={{ border: `2px solid ${gd._id === selectedSimilarGd ? "#111827" : "#e5e7eb"}`, borderRadius: 10, padding: "14px 16px", cursor: "pointer", background: gd._id === selectedSimilarGd ? "#f9fafb" : "#fff", transition: "border-color 0.15s" }}>
                    <p style={{ fontFamily: "Nunito", fontWeight: 700, fontSize: 14, color: "#111827", margin: "0 0 4px" }}>{gd.CompanyName}</p>
                    <p style={{ fontFamily: "Nunito", fontSize: 12, color: "#6b7280", margin: "0 0 8px" }}>{`${gd.City}, ${gd.Country}`}</p>
                    <p style={{ fontFamily: "Nunito", fontSize: 12, color: "#374151", margin: 0 }}>{splitAfterWords(gd.FullDescription || gd.ShortDescription, 40)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: AI assessment + documents */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* AI Verification Assessment */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: aiPrediction || aiError ? 16 : 0 }}>
              <p style={{ fontFamily: "Nunito", fontWeight: 800, fontSize: 13, color: "#374151", margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>AI Assessment</p>
              <button
                onClick={runAiCheck}
                disabled={aiLoading}
                style={{ fontFamily: "Nunito", fontSize: 12, fontWeight: 700, background: "#fff", color: "#111827", border: "1px solid #d1d5db", borderRadius: 8, padding: "6px 14px", cursor: aiLoading ? "default" : "pointer", opacity: aiLoading ? 0.6 : 1 }}>
                {aiLoading ? "Running…" : aiPrediction ? "Re-run" : "Run AI Check"}
              </button>
            </div>

            {aiError && (
              <p style={{ fontFamily: "Nunito", fontSize: 13, color: "#b91c1c", margin: 0 }}>{aiError}</p>
            )}

            {aiPrediction && (
              <div>
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                  <span style={{
                    fontFamily: "Nunito", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5,
                    padding: "5px 12px", borderRadius: 20,
                    background: verdictColors[aiPrediction.verdict]?.bg,
                    border: `1px solid ${verdictColors[aiPrediction.verdict]?.border}`,
                    color: verdictColors[aiPrediction.verdict]?.text,
                  }}>
                    {aiPrediction.verdict?.replace("_", " ")}
                  </span>
                  <span style={{ fontFamily: "Nunito", fontSize: 13, fontWeight: 700, color: "#6b7280" }}>Score: {aiPrediction.score}/100</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={infoRow}><span style={labelStyle}>Website</span><span style={valueStyle}>
                    {aiPrediction.checks?.website?.reachable ? "Reachable" : "Unreachable"}
                    {aiPrediction.checks?.website?.has_ssl ? ", SSL OK" : ""}
                    {aiPrediction.checks?.website?.domain_age_days != null ? `, domain age ${aiPrediction.checks.website.domain_age_days}d` : ""}
                  </span></div>
                  <div style={infoRow}><span style={labelStyle}>Name Match</span><span style={valueStyle}>
                    {aiPrediction.checks?.name_match_vs_website?.reason || "—"}
                  </span></div>
                  <div style={infoRow}><span style={labelStyle}>About Match</span><span style={valueStyle}>
                    {aiPrediction.checks?.about?.reason || "—"}
                  </span></div>
                  <div style={{ ...infoRow, borderBottom: "none" }}><span style={labelStyle}>LinkedIn</span><span style={valueStyle}>
                    {aiPrediction.checks?.linkedin?.valid_format
                      ? (aiPrediction.checks.linkedin.reachable ? "Valid, reachable" : "Valid format, unreachable")
                      : "Invalid format"}
                  </span></div>
                </div>
                <p style={{ fontFamily: "Nunito", fontSize: 11, color: "#9ca3af", margin: "14px 0 0" }}>
                  Advisory only — verify manually before deciding.
                </p>
              </div>
            )}
          </div>

          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px" }}>
            <p style={{ fontFamily: "Nunito", fontWeight: 800, fontSize: 13, color: "#374151", margin: "0 0 12px", textTransform: "uppercase", letterSpacing: 1 }}>Documents</p>
            {document && document.documents && document.documents.length > 0
              ? document.documents.map((v, i) => (
                <a key={i} href={v.location} target="_blank" rel="noreferrer"
                  style={{ display: "block", marginBottom: 8, fontFamily: "Nunito", fontSize: 13, fontWeight: 700, color: "#111827", background: "#f3f4f6", borderRadius: 8, padding: "10px 14px", textDecoration: "none", border: "1px solid #e5e7eb" }}>
                  Document {i + 1} →
                </a>
              ))
              : <p style={{ fontFamily: "Nunito", fontSize: 13, color: "#9ca3af", margin: 0 }}>No documents found</p>}
            {document && document.photoid && (
              <a href={document.photoid} target="_blank" rel="noreferrer"
                style={{ display: "block", marginTop: 8, fontFamily: "Nunito", fontSize: 13, fontWeight: 700, color: "#111827", background: "#f3f4f6", borderRadius: 8, padding: "10px 14px", textDecoration: "none", border: "1px solid #e5e7eb" }}>
                Photo ID →
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Send Review — full width below */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "24px 28px", marginTop: 20 }}>
        <p style={{ fontFamily: "Nunito", fontWeight: 800, fontSize: 13, color: "#374151", margin: "0 0 12px", textTransform: "uppercase", letterSpacing: 1 }}>Send Review</p>
        <textarea
          rows={5}
          placeholder="Write feedback for the founder…"
          onChange={(e) => setreview(e.target.value)}
          value={review}
          style={{ width: "100%", boxSizing: "border-box", fontFamily: "Nunito", fontSize: 14, color: "#111827", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", resize: "vertical", outline: "none", lineHeight: 1.6 }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <button onClick={sendReview}
            style={{ fontFamily: "Nunito", fontSize: 13, fontWeight: 700, background: "#111827", color: "#fff", border: "none", borderRadius: 8, padding: "10px 28px", cursor: "pointer" }}>
            Send Review
          </button>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default FounderPage;
